import { useEffect } from 'react';

import { isDesktopApp } from '@frontend/utils/desktop';

import { useBadgeCount } from './useBadgeCount';

/**
 * Mirrors the unread notification count onto the desktop app's dock/taskbar
 * icon badge (like Teams/Slack). The web build has no such icon, so this is a
 * no-op unless running inside the Electron shell and the shell is new enough to
 * expose `setBadgeCount` (older shells lack it — gate on it).
 *
 * Uses the same grouped badge count as the favicon so every surface (bell, nav,
 * favicon, dock) shows one consistent number.
 */
export function useDesktopBadge() {
  const { data: badgeCount } = useBadgeCount({ grouped: true });
  const count = badgeCount ?? 0;

  useEffect(() => {
    if (!isDesktopApp()) {
      return;
    }
    void window.desktopAPI?.app?.setBadgeCount?.(count);
  }, [count]);

  useEffect(() => {
    // Clear the badge when the app unmounts (e.g. sign-out) so a stale count
    // doesn't linger on the dock icon.
    return () => {
      if (isDesktopApp()) {
        void window.desktopAPI?.app?.setBadgeCount?.(0);
      }
    };
  }, []);
}
